import { useEffect, useState } from 'react';
import { useRGS } from './useRGS';
import { useGameStore } from '@stores/gameStore';

interface LaunchParams {
  token: string | null;
  mode: 'REAL' | 'DEMO' | null;
  operator: string | null;
  gameCode: string | null;
  language: string | null;
  currency: string | null;
  /**
   * R22-3: currency precision supplied by the launcher (mirror of
   * `ShellConfig.currencyDecimals` from game-shell-contract 0.6.0). When
   * omitted, format helpers fall back to the local CURRENCY_DECIMALS
   * registry derived from `currency`.
   */
  currencyDecimals: number | null;
  rgsUrl: string | null;
  // Player/session info from platform
  balance: number | null;
  playerId: string | null;
  playerName: string | null;
  sessionId: string | null;
}

/**
 * Derive RGS WebSocket URL from current domain
 * RGS Socket.IO is available at the same domain via Traefik routing
 * The /socket.io/ path is routed to the RGS service
 */
function getRgsUrlFromDomain(): string | null {
  const hostname = window.location.hostname;

  // If running on omnitronix.co domain, use the same domain for WebSocket
  // Traefik routes /socket.io/ to the RGS service on these domains
  if (hostname.endsWith('.omnitronix.co') || hostname === 'omnitronix.co') {
    // Use wss for https, ws for http (should always be https in production)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${hostname}`;
  }

  // Local development or other domains - return null to use env/default
  return null;
}

/**
 * Extract launch parameters from URL query string
 * Platform launches game with: ?token=xxx&mode=REAL&operator=xxx&gameCode=xxx
 */
function extractLaunchParams(): LaunchParams {
  const params = new URLSearchParams(window.location.search);

  // R22-3: accept `currencyDecimals` as an optional integer override before
  // using it to decode the balance (balance wire format is major units;
  // we convert to minor units using the advertised precision).
  const decimalsStr = params.get('currencyDecimals');
  const parsedDecimals = decimalsStr ? parseInt(decimalsStr, 10) : NaN;
  const currencyDecimals =
    Number.isInteger(parsedDecimals) && parsedDecimals >= 0 && parsedDecimals <= 20
      ? parsedDecimals
      : null;

  // Parse balance - platform sends it in major units (e.g., 19999.03 EUR, or
  // 0.00010000 BTC). Convert to minor units for internal use by multiplying
  // by 10^decimals. For unknown currencies this still defaults to 2 (cents).
  const balanceStr = params.get('balance');
  const currency = params.get('currency');
  const effectiveDecimals =
    currencyDecimals ?? (() => {
      // Local duplicate of registry to avoid a dynamic import in this hook.
      if (!currency) return 2;
      const upper = currency.toUpperCase();
      const reg: Record<string, number> = {
        EUR: 2, USD: 2, GBP: 2, CAD: 2, AUD: 2, NZD: 2, CHF: 2,
        SEK: 2, NOK: 2, DKK: 2, PLN: 2, JPY: 0, KRW: 0,
        BTC: 8, ETH: 18, USDT: 6, USDC: 6, DOGE: 8, XRP: 6,
        TRX: 6, BNB: 18, SOL: 9, LTC: 8,
      };
      return reg[upper] ?? 2;
    })();
  const balance = balanceStr
    ? Math.round(parseFloat(balanceStr) * Math.pow(10, effectiveDecimals))
    : null;

  return {
    token: params.get('token') || params.get('authToken'), // Support both token and authToken
    mode: params.get('mode') as 'REAL' | 'DEMO' | null,
    operator: params.get('operator') || params.get('operatorCode'), // Support both formats
    gameCode: params.get('gameCode'),
    language: params.get('language'),
    currency,
    currencyDecimals,
    rgsUrl: params.get('rgsUrl'),
    // Player/session info from platform
    balance,
    playerId: params.get('playerId'),
    playerName: params.get('playerName'),
    sessionId: params.get('sessionId'),
  };
}

/**
 * Hook to initialize game session when app loads
 * Extracts token from URL and connects to RGS
 */
export function useGameSession(): {
  isInitializing: boolean;
  error: string | null;
  launchParams: LaunchParams;
} {
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [launchParams] = useState<LaunchParams>(() => extractLaunchParams());

  const { connect, isConnecting } = useRGS();
  const { connectionStatus, sessionId, setBalance, setSessionId, setCurrency, setCurrencyDecimals } = useGameStore();

  useEffect(() => {
    const initSession = async (): Promise<void> => {
      try {
        // R22-3: seed currency + precision FIRST so formatters have the
        // right decimals before the first balance is rendered.
        if (launchParams.currency) {
          setCurrency(launchParams.currency);
        }
        if (launchParams.currencyDecimals !== null) {
          setCurrencyDecimals(launchParams.currencyDecimals);
        }

        // Set balance and session from URL params immediately (before RGS connection)
        // This ensures the UI shows correct values right away
        if (launchParams.balance !== null) {
          console.log('[GameSession] Setting balance from URL params:', launchParams.balance);
          setBalance(launchParams.balance);
        }
        if (launchParams.sessionId) {
          console.log('[GameSession] Setting sessionId from URL params:', launchParams.sessionId);
          setSessionId(launchParams.sessionId);
        }

        // Check for token in URL
        if (!launchParams.token) {
          // In development, we might not have a token
          // In production, this is an error
          if (import.meta.env.PROD) {
            setError('No launch token provided. Please launch the game from the platform.');
            setIsInitializing(false);
            return;
          }

          // Development mode: use a demo token or skip
          console.warn('[GameSession] No token found, running in demo mode');
          setIsInitializing(false);
          return;
        }

        // Connect to RGS with the token
        const operatorId = launchParams.operator ?? 'default';

        // Determine RGS URL: explicit param > domain-derived > env/localhost
        // Frontend connects directly to RGS, RGS handles wallet via its adapter
        const rgsUrl = launchParams.rgsUrl ?? getRgsUrlFromDomain();
        console.log('[GameSession] Connecting to RGS...', { operatorId, rgsUrl });

        connect(launchParams.token, operatorId, rgsUrl);
      } catch (err) {
        console.error('[GameSession] Failed to initialize:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize game session');
        setIsInitializing(false);
      }
    };

    initSession();
  }, [
    launchParams.token,
    launchParams.operator,
    launchParams.balance,
    launchParams.sessionId,
    launchParams.rgsUrl,
    launchParams.currency,
    launchParams.currencyDecimals,
    connect,
    setBalance,
    setSessionId,
    setCurrency,
    setCurrencyDecimals,
  ]);

  // Update initializing state based on connection
  useEffect(() => {
    if (connectionStatus === 'connected' && sessionId) {
      setIsInitializing(false);
      setError(null);
    } else if (connectionStatus === 'error') {
      setIsInitializing(false);
    }
  }, [connectionStatus, sessionId]);

  // If still connecting, consider it initializing
  useEffect(() => {
    if (isConnecting) {
      setIsInitializing(true);
    }
  }, [isConnecting]);

  return {
    isInitializing,
    error,
    launchParams,
  };
}
